import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import Question from './Question';

export default function Assessment({ tokenData, onComplete }) {
  const [questions, setQuestions]   = useState([]);
  const [current, setCurrent]       = useState(0);
  const [responses, setResponses]   = useState({});
  const [loading, setLoading]       = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadQuestions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadQuestions() {
    const { data: universal } = await supabase
      .from('engine_survey_questions')
      .select('*')
      .eq('is_universal', true)
      .eq('is_active', true)
      .order('sort_order');

    const { data: occupancyLinks } = await supabase
      .from('engine_survey_question_occupancies')
      .select('question_code')
      .eq('occupancy_code', tokenData.occupancy_code);

    let occupancyQuestions = [];
    if (occupancyLinks?.length) {
      const codes = occupancyLinks.map(l => l.question_code);
      const { data: oqs } = await supabase
        .from('engine_survey_questions')
        .select('*')
        .in('question_code', codes)
        .eq('is_universal', false)
        .eq('is_active', true)
        .order('sort_order');
      occupancyQuestions = oqs || [];
    }

    setQuestions([...(universal || []), ...occupancyQuestions]);
    setLoading(false);
  }

  function handleResponse(questionCode, value, photoKey = null) {
    setResponses(prev => ({ ...prev, [questionCode]: { value, photoKey } }));
  }

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const rows = questions.map(q => ({
        survey_token_id  : tokenData.id,
        submission_id    : tokenData.submission_id,
        tenant_id        : tokenData.tenant_id,
        question_code    : q.question_code,
        response_text    : responses[q.question_code]?.value || null,
        response_value   : responses[q.question_code]?.value || null,
        photo_storage_key: responses[q.question_code]?.photoKey || null,
        responded_at     : new Date().toISOString(),
        created_at       : new Date().toISOString(),
      }));

      await supabase.from('survey_responses').insert(rows);
      await supabase.from('survey_tokens')
        .update({ status: 'completed', completed_at: new Date().toISOString() })
        .eq('id', tokenData.id);
      await supabase.from('submissions')
        .update({ status: 'under_review', updated_at: new Date().toISOString() })
        .eq('id', tokenData.submission_id);

      onComplete();
    } catch (err) {
      console.error('Submit error:', err);
      alert('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return (
    <div style={{
      height:'100vh', display:'flex', alignItems:'center',
      justifyContent:'center', background:'#0a0a0a',
      color:'#666', fontSize:14, fontFamily:'system-ui',
    }}>
      Loading questions...
    </div>
  );

  const q        = questions[current];
  const total    = questions.length;
  const pct      = Math.round((current / total) * 100);
  const isLast   = current === total - 1;
  const isPhoto  = q?.requires_photo || q?.question_type === 'photo';
  const answered = responses[q?.question_code]?.value != null;

  return (
    <div style={{
      minHeight:'100vh', display:'flex', flexDirection:'column',
      background:'#0a0a0a', fontFamily:'system-ui',
    }}>
      {/* Progress bar */}
      <div style={{ height:3, background:'#1a1a1a' }}>
        <div style={{
          height:'100%', width:`${pct}%`,
          background:'#4ade80', transition:'width 0.3s',
        }}/>
      </div>

      {/* Header */}
      <div style={{
        padding:'16px 24px', display:'flex',
        alignItems:'center', justifyContent:'space-between',
        borderBottom:'1px solid #1a1a1a',
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <div style={{ width:6, height:6, borderRadius:'50%', background:'#4ade80' }}/>
          <span style={{ color:'#fff', fontSize:13, fontWeight:600 }}>Cerebro Risk</span>
        </div>
        <span style={{ fontSize:12, color:'#444' }}>{current + 1} of {total}</span>
      </div>

      {/* Question */}
      <div style={{
        flex:1, padding:'32px 24px',
        maxWidth:560, width:'100%', margin:'0 auto',
        boxSizing:'border-box', overflow:'hidden',
      }}>
        {q && (
          <Question
            key={q.question_code}
            question={q}
            value={responses[q.question_code]?.value || ''}
            tokenData={tokenData}
            onChange={(val, photoKey) => handleResponse(q.question_code, val, photoKey)}
          />
        )}
      </div>

      {/* Navigation */}
      <div style={{
        padding:'16px 24px', borderTop:'1px solid #1a1a1a',
        display:'flex', gap:12, background:'#0a0a0a',
      }}>
        {current > 0 && (
          <button
            onClick={() => setCurrent(c => c - 1)}
            style={{
              flex:1, height:48, borderRadius:8,
              background:'transparent', border:'1px solid #222',
              color:'#666', fontSize:14, cursor:'pointer',
            }}
          >
            ← Back
          </button>
        )}

        {!isLast ? (
          isPhoto ? (
            <div style={{ display:'flex', gap:12, flex:2 }}>
              <button
                onClick={() => { handleResponse(q.question_code, 'skipped'); setCurrent(c => c + 1); }}
                style={{
                  flex:1, height:48, borderRadius:8,
                  background: !answered ? '#1a1a1a' : 'transparent',
                  border: !answered ? '1px solid #4ade80' : '1px solid #222',
                  color: !answered ? '#4ade80' : '#333',
                  fontSize:13, cursor:'pointer', transition:'all 0.2s',
                }}
              >
                Skip Photo
              </button>
              <button
                onClick={() => answered && setCurrent(c => c + 1)}
                style={{
                  flex:2, height:48, borderRadius:8,
                  background: answered ? '#4ade80' : '#141414',
                  border:'none',
                  color: answered ? '#000' : '#333',
                  fontSize:14, fontWeight:600,
                  cursor: answered ? 'pointer' : 'default',
                  transition:'all 0.2s',
                }}
              >
                {answered ? 'Next →' : 'Upload Photo First'}
              </button>
            </div>
          ) : (
            <button
              onClick={() => setCurrent(c => c + 1)}
              style={{
                flex:2, height:48, borderRadius:8,
                background: answered ? '#4ade80' : '#1a1a1a',
                border:'none',
                color: answered ? '#000' : '#444',
                fontSize:14, fontWeight:600, cursor:'pointer',
                transition:'all 0.2s',
              }}
            >
              Next →
            </button>
          )
        ) : (
          <button
            onClick={handleSubmit}
            disabled={submitting}
            style={{
              flex:2, height:48, borderRadius:8,
              background: submitting ? '#1a1a1a' : '#4ade80',
              border:'none',
              color: submitting ? '#444' : '#000',
              fontSize:14, fontWeight:600, cursor:'pointer',
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Assessment ✓'}
          </button>
        )}
      </div>
    </div>
  );
}
